/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import isAbortedRequest from '../../helpers/isAbortedRequest';
import { fetchListingAssets } from './fetchListingAssets';
import { fetchVariantAssets } from './fetchVariantAssets';
import { refreshListingVariantByMaster } from './refreshListingVariantByMaster';
import { getListingChannelId } from './helpers/channelHelpers';
import { findListingEntity } from './helpers/entityReadHelpers';
import {
  upsertListing,
  replaceListingVariantIndexForMaster,
  syncListingVariantsFromIndex,
} from './helpers/entityAdapter';
import removeListingChannelEntities from './helpers/removeListingChannelEntities';
import {
  isStaleListingsRequest,
  setActiveTabByProductChannel,
  setListingsCardLoaded,
  setListingsCardLoading,
  setListingsLatestRequestId,
  setLiveRefreshingByProduct,
} from './helpers/uiAdapter';
import {
  showChannelGoneToastFromItems,
  showChannelGoneToastsFromRefresh,
} from './helpers/showChannelProductGoneToast';

const hasLiveStaleFlag = (listings) => (
  (listings || []).some((listing) => Boolean(listing?.liveStale))
);

const normalizeListingsCardPayload = (data) => {
  if (Array.isArray(data)) {
    return { listings: data, listingVariantIndex: [] };
  }

  return {
    listings: Array.isArray(data?.listings) ? data.listings : [],
    listingVariantIndex: Array.isArray(data?.listingVariantIndex) ? data.listingVariantIndex : [],
  };
};

const toIndexEntity = (row, masterProductId) => {
  const listingVariantId = row.listingVariantId || row._id;
  if (!listingVariantId) return null;

  return {
    _id: listingVariantId,
    listingVariantId,
    listingProductId: row.listingProductId,
    masterProductId,
    masterVariantId: row.masterVariantId,
    salesChannelId: row.salesChannelId,
    listingStatus: row.listingStatus,
    live: row.live,
  };
};

const snapshotListingsForMaster = (productsState, masterProductId) => (
  Object.values(productsState?.entities?.listings || {}).filter(
    (listing) => String(listing.masterProductId) === String(masterProductId),
  )
);

const snapshotIndexForMaster = (productsState, masterProductId) => (
  Object.values(productsState?.entities?.listingVariantIndex || {}).filter(
    (row) => String(row.masterProductId) === String(masterProductId),
  )
);

const refetchLoadedListingMedia = ({
  dispatch,
  productsState,
  listings,
  listingVariantIndex,
  masterProductId,
  masterVariantId,
}) => {
  (listings || []).forEach((listing) => {
    if (productsState?.entities?.assets?.listingAssets?.[listing._id] === undefined) return;
    const channelId = getListingChannelId(listing);
    if (!channelId) return;
    dispatch(fetchListingAssets({
      masterProductId,
      channelId,
      listingProductId: listing._id,
    }));
  });

  if (!masterVariantId) return;

  const indexRow = (listingVariantIndex || []).find(
    (row) => String(row.masterVariantId) === String(masterVariantId),
  );
  const listingVariantId = indexRow?.listingVariantId || indexRow?._id;
  if (!listingVariantId) return;
  if (productsState?.entities?.assets?.listingVariantAssets?.[listingVariantId] === undefined) {
    return;
  }

  dispatch(fetchVariantAssets({
    productId: masterProductId,
    variantId: listingVariantId,
    channelId: indexRow.salesChannelId,
    masterVariantId,
  }));
};

export const getListings = createAsyncThunk(
  'products/getListings',
  async (
    { masterProductId, skipLiveRefreshPoll = false, masterVariantId = null },
    { dispatch, getState, extra: { Logger, Toast, sdk }, signal },
  ) => {
    try {
      const productsState = getState().products;
      const previousListings = snapshotListingsForMaster(productsState, masterProductId);
      const previousIndex = snapshotIndexForMaster(productsState, masterProductId);

      const { data } = await sdk.products.getListingsCard({ masterProductId }, signal);
      if (isAbortedRequest(null, signal)) {
        throw new DOMException('Aborted', 'AbortError');
      }
      const { listings, listingVariantIndex } = normalizeListingsCardPayload(data);

      const flagKind = showChannelGoneToastFromItems({
        Toast,
        items: [...listings, ...listingVariantIndex],
      });
      if (!flagKind) {
        showChannelGoneToastsFromRefresh({
          Toast,
          previousListings,
          nextListings: listings,
          previousIndex,
          nextIndex: listingVariantIndex,
        });
      }

      if (masterVariantId) {
        await dispatch(refreshListingVariantByMaster({
          masterProductId,
          masterVariantId,
        }));
      }

      if (skipLiveRefreshPoll) {
        refetchLoadedListingMedia({
          dispatch,
          productsState: getState().products,
          listings,
          listingVariantIndex,
          masterProductId,
          masterVariantId,
        });
      }

      return { listings, listingVariantIndex };
    } catch (error) {
      if (isAbortedRequest(error, signal)) throw error;
      Logger.log('UI', 'ToastProductGetFailed');
      throw error;
    }
  },
);

export const ensureChannelListingsFresh = async ({
  dispatch,
  getState,
  masterProductId,
  masterVariantId = null,
}) => {
  const isRefreshing = Boolean(
    getState()?.products?.ui?.liveRefreshingByProductId?.[masterProductId],
  );
  if (!isRefreshing) return;

  await dispatch(getListings({
    masterProductId,
    masterVariantId,
    skipLiveRefreshPoll: true,
  })).unwrap();
};

export const reducer = (builder) => {
  builder
    .addCase(getListings.pending, (state, { meta }) => {
      setListingsLatestRequestId(state, meta.arg?.masterProductId, meta.requestId);
      if (meta.arg?.skipLiveRefreshPoll) return;
      state.error = null;
      setListingsCardLoading(state, meta.arg?.masterProductId, true);
    })
    .addCase(getListings.fulfilled, (state, { payload, meta }) => {
      const { masterProductId } = meta.arg;
      if (isStaleListingsRequest(state, masterProductId, meta.requestId)) return;
      const { listings, listingVariantIndex } = normalizeListingsCardPayload(payload);
      const incomingIds = new Set(listings.map((listing) => String(listing._id)));

      setListingsCardLoaded(state, masterProductId);
      setLiveRefreshingByProduct(state, masterProductId, hasLiveStaleFlag(listings));
      const indexRows = listingVariantIndex
        .map((row) => toIndexEntity(row, masterProductId))
        .filter(Boolean);
      replaceListingVariantIndexForMaster(state, masterProductId, indexRows);
      syncListingVariantsFromIndex(state, indexRows);

      Object.values(state.entities?.listings || {}).forEach((listing) => {
        if (String(listing.masterProductId) !== String(masterProductId)) return;
        if (incomingIds.has(String(listing._id))) return;
        removeListingChannelEntities(state, listing);
      });

      listings.forEach((listing) => {
        const channelId = getListingChannelId(listing);
        if (!channelId) return;

        const existingListing = findListingEntity(state, { masterProductId, channelId });
        const existingAssets = state.entities?.assets?.listingAssets?.[listing._id];
        const activeTab = state.ui?.activeTabByProductChannel?.[masterProductId]?.[channelId]
          || existingListing?.listingStatus
          || listing.listingStatus;

        const listingProduct = { ...listing };
        delete listingProduct.channelProductGone;
        delete listingProduct.liveStale;
        if (existingAssets !== undefined) {
          listingProduct.assets = existingAssets;
        }

        setActiveTabByProductChannel(state, masterProductId, channelId, activeTab);

        upsertListing(state, {
          ...listingProduct,
          masterProductId,
        });
      });
    })
    .addCase(getListings.rejected, (state, { error, meta }) => {
      if (meta.arg?.skipLiveRefreshPoll) return;
      if (isStaleListingsRequest(state, meta.arg?.masterProductId, meta.requestId)) return;
      setListingsCardLoading(state, meta.arg?.masterProductId, false);
      if (meta.aborted) return;
      state.error = error;
      setListingsCardLoaded(state, meta.arg?.masterProductId);
    });
};
